"use client";

import JournalOverview from "@/src/components/journal-site/home/journal-overview/journal-overview";
import JournalMetrics from "@/src/components/journal-site/home/journal-metrics/journal-metrics";
import { Box } from "@mui/material";
import { ReactNode } from "react";
import CardWidget from "@/src/components/journal-site/common/card-widget/card-widget";
import ArticleList from "@/src/_types/Article/article-list";
import InPress from "@/src/_types/in-press";
import lastArticles from "@/src/_types/last-article";
import InPressArticles from "@/src/components/journal-site/home/in-press-articles/in-press-articles";
import {
  IN_PRESS_ARTICLES,
  MOST_CITED_ARTICLES,
  MOST_DOWNLOADED_ARTICLES,
  MOST_VIEWED_ARTICLES,
  RECENT_ARTICLES_TITLE,
} from "@/src/constant/constant";
import Index from "@/src/components/journal-site/home/index";
import Articles from "@/src/components/journal-site/common/articles/articles";
import { MapToDocumentListItems } from "@/src/utils/database-helpers/mapper/document-mapper";
import Issue from "@/src/_types/issue";
import Page from "@/src/_types/Page/page";
import RankedArticle from "@/src/_types/Article/ranked-article";
import RankedArticlesSection from "@/src/components/journal-site/ranked-articles/ranked-articles-section";
import BroadsheetTabs from "@/src/components/common/broadsheet/tabs";
import type { BroadsheetTabItem } from "@/src/components/common/broadsheet/tabs";
import { journalMostCitedHref } from "@/src/utils/paths";
import { getXmlIssueDownloadLink } from "@/src/utils/journal-directory-helper";
import {
  doubleRule,
  RAIL_RIGHT,
  SHEET_GUTTER,
} from "@/src/components/common/broadsheet/tokens";

function fetchInPressDocuments(documents: any[], journal: Journal): InPress {
  const inPressDocuments: ArticleList[] = MapToDocumentListItems({
    journal,
    documents,
  });
  inPressDocuments.sort((a, b) => {
    return a.display_order - b.display_order;
  });

  return {
    articles: inPressDocuments,
  };
}

function fetchLastDocuments(
  documents: any[],
  journal: Journal,
  lastIssue: Issue,
): lastArticles {
  const lastDocuments: ArticleList[] = MapToDocumentListItems({
    journal,
    documents,
  });
  lastDocuments.sort((a, b) => {
    return a.display_order - b.display_order;
  });

  return {
    articles: lastDocuments,
    caption: lastIssue?.caption,
    volume: lastIssue?.volume_name ?? "",
    date: lastIssue?.formatted_publish_date,
    xmlLink: getXmlIssueDownloadLink(journal, lastIssue?.id),
  };
}

export default function JournalSiteHome({
  journal,
  lastIssue,
  lastDocumentsData,
  inPressDocumentsData,
  mostCitedDocuments,
  mostViewedDocuments,
  mostDownloadedDocuments,
  widgets,
  welcomeMsg,
}: {
  journal: any;
  lastIssue: any;
  lastDocumentsData: any;
  inPressDocumentsData: any;
  mostCitedDocuments?: RankedArticle[];
  mostViewedDocuments?: RankedArticle[];
  mostDownloadedDocuments?: RankedArticle[];
  widgets: Page[];
  welcomeMsg: any;
}) {
  const inPressDocuments = fetchInPressDocuments(inPressDocumentsData, journal);
  const lastDocuments = fetchLastDocuments(
    lastDocumentsData,
    journal,
    lastIssue,
  );
  const hasRail = (widgets?.length ?? 0) > 0;
  const mostCited = mostCitedDocuments ?? [];
  const mostViewed = mostViewedDocuments ?? [];
  const mostDownloaded = mostDownloadedDocuments ?? [];
  const recentArticles = lastDocuments.articles ?? [];
  const inPressArticles = inPressDocuments.articles ?? [];

  const sections: {
    id: string;
    label: string;
    render: (hideHeading: boolean) => ReactNode;
  }[] = [
    ...(recentArticles.length > 0
      ? [
          {
            id: "recent",
            label: RECENT_ARTICLES_TITLE,
            render: (hideHeading: boolean) => (
              <Articles
                hideHeading={hideHeading}
                title={RECENT_ARTICLES_TITLE}
                articles={recentArticles}
                date={lastDocuments.date}
                volume={lastDocuments.volume}
                caption={lastDocuments.caption}
                desc={
                  journal.options?.template_configs?.journal_recent_article
                    ?.recent_article?.caption
                }
              />
            ),
          },
        ]
      : []),
    ...(inPressArticles.length > 0
      ? [
          {
            id: "in-press",
            label: IN_PRESS_ARTICLES,
            render: (hideHeading: boolean) => (
              <InPressArticles
                hideHeading={hideHeading}
                title={IN_PRESS_ARTICLES}
                articles={inPressArticles}
              />
            ),
          },
        ]
      : []),
    ...(mostCited.length > 0
      ? [
          {
            id: "most-cited",
            label: MOST_CITED_ARTICLES,
            render: (hideHeading: boolean) => (
              <RankedArticlesSection
                hideHeading={hideHeading}
                metric="citations"
                title={MOST_CITED_ARTICLES}
                articles={mostCited}
                action={{
                  label: "All most cited",
                  href: journalMostCitedHref(journal),
                }}
              />
            ),
          },
        ]
      : []),
    ...(mostViewed.length > 0
      ? [
          {
            id: "most-viewed",
            label: MOST_VIEWED_ARTICLES,
            render: (hideHeading: boolean) => (
              <RankedArticlesSection
                hideHeading={hideHeading}
                metric="views"
                title={MOST_VIEWED_ARTICLES}
                articles={mostViewed}
              />
            ),
          },
        ]
      : []),
    ...(mostDownloaded.length > 0
      ? [
          {
            id: "most-downloaded",
            label: MOST_DOWNLOADED_ARTICLES,
            render: (hideHeading: boolean) => (
              <RankedArticlesSection
                hideHeading={hideHeading}
                metric="downloads"
                title={MOST_DOWNLOADED_ARTICLES}
                articles={mostDownloaded}
              />
            ),
          },
        ]
      : []),
  ];

  const tabs: BroadsheetTabItem[] = sections.map((section) => ({
    id: section.id,
    label: section.label,
    content: section.render(true),
  }));

  return (
    <Box
      sx={{
        px: SHEET_GUTTER,
        pt: { xs: 1, md: 1.25 },
        pb: { xs: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          pb: { xs: 1, md: 1.25 },
          ...doubleRule,
        }}
      >
        <JournalOverview journal={journal} />
        <JournalMetrics journal={journal} variant="band" />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: hasRail ? `1fr ${RAIL_RIGHT}` : "1fr",
          },
          columnGap: 2.5,
          rowGap: 1.5,
          alignItems: "start",
          "& > *": { minWidth: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {sections.length > 1 ? (
            <BroadsheetTabs label="Article lists" items={tabs} />
          ) : (
            sections[0]?.render(false)
          )}

          {recentArticles.length === 0 ? (
            <Index journal={journal} welcomeMsg={welcomeMsg} />
          ) : null}
        </Box>

        {hasRail ? (
          <Box
            component="aside"
            aria-label="About this journal"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              pl: { lg: 1.25 },
              borderLeft: { lg: "1px solid" },
              borderColor: { lg: "custom.ruleHairline" },
            }}
          >
            {widgets.map((value, index) => {
              return <CardWidget value={value} key={index} />;
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
